import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import {
  fetchActiveEntitlements,
  fetchActiveRoles,
  fetchOwnProfile,
  hasAnyRole,
  hasEntitlement,
} from "@/lib/auth";
import {
  fetchActivePermissions,
  hasAnyPermission,
  hasPermission,
  type ActivePermission,
  type PermissionKey,
} from "@/lib/permissions";
import type { ActiveEntitlement, ActiveUserRole, Profile, RoleName } from "@/types/database";

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: ActiveUserRole[];
  entitlements: ActiveEntitlement[];
  permissions: ActivePermission[];
  isLoading: boolean;
  isAuthorizing: boolean;
  isAuthenticated: boolean;
  hasRole: (allowed: RoleName[]) => boolean;
  hasPermission: (key: PermissionKey) => boolean;
  hasAnyPermission: (keys: PermissionKey[]) => boolean;
  hasEntitlement: (key: string) => boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<ActiveUserRole[]>([]);
  const [entitlements, setEntitlements] = useState<ActiveEntitlement[]>([]);
  const [permissions, setPermissions] = useState<ActivePermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const inFlightUserIdRef = useRef<string | null>(null);

  const loadUserData = useCallback(async (userId: string) => {
    if (inFlightUserIdRef.current === userId) return;
    inFlightUserIdRef.current = userId;
    setIsAuthorizing(true);

    try {
    // These three datasets are independent and can be fetched together.
    // Previously they were awaited one after another, adding multiple network
    // round trips to every authenticated cold start.
    const [profileResult, rolesResult, entitlementsResult] = await Promise.all([
      fetchOwnProfile(userId).catch((err) => {
        console.error("Failed to load profile:", err);
        return null;
      }),
      fetchActiveRoles(userId).catch((err) => {
        console.error("Failed to load roles:", err);
        return [];
      }),
      fetchActiveEntitlements(userId).catch((err) => {
        console.error("Failed to load entitlements:", err);
        return [];
      }),
    ]);

    // Permissions depend on the resolved role IDs, so this remains the only
    // dependent request in the bootstrap sequence.
    const permissionResult = await fetchActivePermissions(
      rolesResult.map((role) => role.role_id),
    ).catch((err) => {
      console.error("Failed to load permissions:", err);
      return [];
    });

    setProfile(profileResult);
    setRoles(rolesResult);
    setEntitlements(entitlementsResult);
    setPermissions(permissionResult);
    } finally {
      inFlightUserIdRef.current = null;
      setIsAuthorizing(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (session?.user.id) await loadUserData(session.user.id);
  }, [session?.user.id, loadUserData]);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session: initial } }) => {
      if (!isMounted) return;
      setSession(initial);
      setIsLoading(false);
      if (initial?.user.id) void loadUserData(initial.user.id);
    }).catch((error) => {
      console.error("Failed to restore Supabase session:", error);
      if (isMounted) {
        setSession(null);
        setIsLoading(false);
        setIsAuthorizing(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);

      if (nextSession?.user.id) {
        void loadUserData(nextSession.user.id);
      } else {
        setProfile(null);
        setRoles([]);
        setEntitlements([]);
        setPermissions([]);
      }
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserData]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      roles,
      entitlements,
      permissions,
      isLoading,
      isAuthorizing,
      isAuthenticated: session !== null,
      hasRole: (allowed) => hasAnyRole(roles, allowed),
      hasPermission: (key) => hasPermission(permissions, key),
      hasAnyPermission: (keys) => hasAnyPermission(permissions, keys),
      hasEntitlement: (key) => hasEntitlement(entitlements, key),
      signInWithPassword,
      signOut,
      refresh,
    }),
    [session, profile, roles, entitlements, permissions, isLoading, isAuthorizing, signInWithPassword, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
